import { useEffect, useRef, useState } from 'react';

import { useAuthStore } from '@/features/auth/auth-store';

import { type Referral, useReferralsStore } from './referrals-store';

export interface DenyModalProps {
  /** The referrals being denied (1 = single mode; >1 = batch mode). */
  referrals: Referral[];
  onClose: () => void;
  notify: (title: string, body: string) => void;
}

/**
 * The Deny modal (SCRUM-332) — v6 `.deny-modal-*`: settings-modal chrome, a
 * lede naming the customer (or the batch size), a REQUIRED reason textarea
 * (empty submit triggers the red-border nudge for 1400ms instead of
 * submitting), and a danger-filled confirm button. The reason lands on the
 * referral row as the DENIED sub-block and in the audit events.
 */
export function DenyModal({ referrals, onClose, notify }: DenyModalProps) {
  const [reason, setReason] = useState('');
  const [nudge, setNudge] = useState(false);
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const deny = useReferralsStore((s) => s.deny);
  const user = useAuthStore((s) => s.user);

  const batch = referrals.length > 1;
  const single = referrals[0];

  useEffect(() => {
    const t = setTimeout(() => areaRef.current?.focus(), 50);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const confirm = () => {
    const value = areaRef.current?.value.trim() ?? '';
    if (!value) {
      setNudge(true);
      areaRef.current?.focus();
      setTimeout(() => {
        setNudge(false);
      }, 1400);
      return;
    }
    const by = user?.displayName ?? 'You';
    for (const r of referrals) deny(r.id, value, by);
    notify(
      batch ? 'Referrals denied' : 'Referral denied',
      batch
        ? `${String(referrals.length)} referrals denied — reason logged.`
        : `${single?.customerName ?? ''} — reason logged.`,
    );
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40">
      <div
        role="dialog"
        aria-label={batch ? `Deny ${String(referrals.length)} referrals` : 'Deny referral'}
        className="bg-bg-4 border-border-1 rounded-tk-lg shadow-tk-lg flex max-w-[480px] min-w-[420px] flex-col border"
      >
        <div className="border-border-0 flex items-center justify-between border-b px-5 py-4">
          <span className="font-display text-text-0 text-base font-semibold">
            {batch ? `Deny ${String(referrals.length)} referrals` : 'Deny referral'}
          </span>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="text-text-3 hover:bg-bg-3 hover:text-text-0 rounded-tk-sm grid h-[26px] w-[26px] place-items-center"
          >
            <svg
              viewBox="0 0 12 12"
              width="10"
              height="10"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <path d="M2 2l8 8M10 2l-8 8" />
            </svg>
          </button>
        </div>

        <div className="px-5 py-4">
          <p className="text-text-1 mb-3.5 text-[0.8125rem] leading-[1.55]">
            {batch ? (
              <>
                Denying{' '}
                <strong className="text-text-0 font-semibold">
                  {referrals.length} selected referrals
                </strong>{' '}
                (New / Under review only).
              </>
            ) : (
              <>
                Denying the referral for{' '}
                <strong className="text-text-0 font-semibold">{single?.customerName}</strong>.
              </>
            )}{' '}
            Add a short reason — the original submitter will see it on the referral row, and it
            becomes part of the customer’s historical record.
          </p>

          <span className="text-text-3 font-mono text-[0.625rem] font-semibold tracking-[0.06em] uppercase">
            Reason for denial
            <span aria-hidden="true" className="text-danger ml-1.5">
              required
            </span>
          </span>
          <textarea
            ref={areaRef}
            value={reason}
            rows={3}
            aria-label="Reason for denial"
            placeholder="e.g. Customer already holds this product · ineligible per credit policy · prior outreach declined"
            onChange={(e) => {
              setReason(e.target.value);
            }}
            style={nudge ? { borderColor: 'var(--danger)' } : undefined}
            className="bg-bg-4 border-border-1 rounded-tk-md text-text-0 placeholder:text-text-3 mt-1.5 min-h-20 w-full resize-y border px-3 py-2.5 text-sm leading-normal outline-none focus:border-[var(--cyan)]"
          />
        </div>

        <div className="border-border-0 flex justify-end gap-2 border-t px-5 pt-3 pb-4">
          <button
            type="button"
            onClick={onClose}
            className="bg-bg-3 border-border-1 rounded-tk-sm text-text-1 hover:bg-bg-2 hover:text-text-0 border px-3 py-[5px] text-xs"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirm}
            className="bg-danger rounded-tk-md px-3.5 py-[7px] text-xs font-semibold text-white hover:bg-[#b91c1c]"
          >
            Deny referral
          </button>
        </div>
      </div>
    </div>
  );
}
